import type {
  JobDescriptionContentSchema,
  RecruitingEmploymentContractType,
  SiteJobDescriptionPublicDetailResponse,
} from '@flex-apis/recruiting';
import { isoDatePartToDate } from '@flex-packages/date';
import {
  generateAttachmentDocument,
  generateCandidatePreQuestion,
  RecruitingPeriodType,
  type AttachmentDocument,
  type CandidatePreQuestion,
  type CandidateRequiredInformation,
} from '@flex-private-packages/recruiting-common/base';

export interface PublicJobDescriptionDetail {
  readonly title: string;
  readonly jobDescriptionIdHash: string;
  readonly contractType: RecruitingEmploymentContractType | null;
  readonly jobRoleName?: string;
  readonly periodType: RecruitingPeriodType;
  readonly startDate: Date | null;
  readonly endDate: Date | null;

  // JobDescriptionContent
  readonly content: {
    schema: JobDescriptionContentSchema;
    data: string;
  };

  // AttachmentDocument
  readonly documentList: AttachmentDocument[];

  // PreQuestion
  readonly preQuestionList: Array<CandidatePreQuestion>;

  // JobDescriptionRequired
  readonly requiredInformation: CandidateRequiredInformation;
}

export function generatePublicJobDescriptionDetail(
  response: SiteJobDescriptionPublicDetailResponse
): PublicJobDescriptionDetail {
  return {
    title: response.title,
    jobDescriptionIdHash: response.jobDescriptionIdHash,
    contractType: response.recruitingEmploymentContractType,
    jobRoleName: response.jobRoleName,
    periodType: response.isOccasionalRecruitment
      ? RecruitingPeriodType.Occasional
      : RecruitingPeriodType.Periodic,
    startDate: response.recruitingStartDate
      ? isoDatePartToDate(response.recruitingStartDate)
      : null,
    endDate: response.recruitingEndDate
      ? isoDatePartToDate(response.recruitingEndDate)
      : null,

    // JobDescriptionContent
    content: {
      schema: response.content.schema,
      data: response.content.data,
    },

    // AttachmentDocument
    documentList: [...response.attachmentDocuments]
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map(generateAttachmentDocument),

    // PreQuestion
    preQuestionList: [...response.preQuestions]
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map(generateCandidatePreQuestion),

    // JobDescriptionRequired
    requiredInformation: {
      education: response.requiredEducation,
      veteran: response.requiredVeteran,
      work: response.requiredWork,
      militaryService: response.requiredMilitaryService,
      handicapCase: response.requiredHandicapCase,
    },
  };
}
